import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { PluginKey, type EditorState } from '@atlaskit/editor-prosemirror/state';

export type ThinkingStatus = 'idle' | 'thinking' | 'complete' | 'error';

/** Error taxonomy, aligned to inline chat's `message_template`. `UNKNOWN` is the fallback. */
export type ThinkingErrorTemplate =
	| 'RATE_LIMIT'
	| 'ACCEPTABLE_USE_VIOLATIONS'
	| 'TOKEN_LIMIT_EXCEEDED'
	| 'UNKNOWN';

export type ThinkingError = {
	template: ThinkingErrorTemplate;
};

export type ReviewResults = {
	count: number;
	hadExistingSuggestions: boolean;
};

export type ThinkingPluginState = {
	baselineIds?: readonly string[];
	/** Set only when status is 'error'; drives the error screen. `null` otherwise. */
	error: ThinkingError | null;
	/**
	 * Progress-bar facade target (0-100) chosen once per thinking session. Kept in
	 * state (rather than randomised per component mount) so all bars fill to the
	 * same value. `null` while idle.
	 */
	progressTarget: number | null;
	reviewResults?: ReviewResults;
	/**
	 * Epoch ms captured when the current thinking session started (status → 'thinking').
	 * Shared by every loading bar so their progress/rotation animations stay in sync
	 * across mount/unmount (e.g. switching between the editor bar and the sidebar bar).
	 * `null` while idle.
	 */
	startedAt: number | null;
	status: ThinkingStatus;
};

export type ThinkingPluginMeta = {
	error?: ThinkingError;
	status: ThinkingStatus;
};

// Facade progress hovers at a randomised target (85-95%) until completion.
const pickProgressTarget = (): number => 85 + Math.random() * 10;

export const thinkingPluginKey: PluginKey<ThinkingPluginState> = new PluginKey<ThinkingPluginState>(
	'aiSuggestionsThinkingPlugin',
);

export function createThinkingPlugin({
	dispatch,
	getSuggestionIds,
}: {
	dispatch: Dispatch;
	getSuggestionIds?: (state: EditorState) => readonly string[] | undefined;
}): SafePlugin<ThinkingPluginState> {
	return new SafePlugin<ThinkingPluginState>({
		key: thinkingPluginKey,
		state: {
			init(): ThinkingPluginState {
				return {
					status: 'idle',
					startedAt: null,
					progressTarget: null,
					error: null,
				};
			},
			apply(tr, pluginState, oldState, newState): ThinkingPluginState {
				let result = pluginState;
				const meta: ThinkingPluginMeta | undefined = tr.getMeta(thinkingPluginKey);

				if (meta && meta.status !== pluginState.status) {
					let startedAt = pluginState.startedAt;
					let progressTarget = pluginState.progressTarget;

					if (meta.status === 'thinking') {
						// Start of a new session: anchor the shared timing/target once.
						startedAt = Date.now();
						progressTarget = pickProgressTarget();
					} else if (meta.status === 'idle') {
						// Session ended/cancelled: clear so the next session starts fresh.
						startedAt = null;
						progressTarget = null;
					}
					// 'complete' and 'error' keep startedAt/progressTarget so the bar can
					// settle its progress before showing the ready / error state.

					// Carry the error payload only into the 'error' state; clear otherwise.
					const error = meta.status === 'error' ? (meta.error ?? { template: 'UNKNOWN' }) : null;

					result = {
						baselineIds:
							meta.status === 'thinking'
								? getSuggestionIds?.(oldState)
								: meta.status === 'idle'
									? undefined
									: pluginState.baselineIds,
						status: meta.status,
						startedAt,
						progressTarget,
						error,
					};
				}

				const suggestionIds = getSuggestionIds?.(newState);
				if (result.status === 'thinking' && !result.baselineIds && suggestionIds) {
					result = { ...result, baselineIds: suggestionIds };
				}
				// Refreshes already update suggestions asynchronously after completion.
				// Keep the count in sync with that existing flow without delaying the bar.
				if (
					result.status === 'complete' &&
					result.baselineIds &&
					suggestionIds &&
					(!result.reviewResults || suggestionIds !== getSuggestionIds?.(oldState))
				) {
					const baseline = new Set(result.baselineIds);
					const count = suggestionIds.filter((id) => !baseline.has(id)).length;
					if (result.reviewResults?.count !== count) {
						result = {
							...result,
							reviewResults: { count, hadExistingSuggestions: baseline.size > 0 },
						};
					}
				}
				if (result !== pluginState) {
					dispatch(thinkingPluginKey, result);
				}
				return result;
			},
		},
	});
}
